import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideExperimentalZonelessChangeDetection
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { LayoutModule } from '@angular/cdk/layout';
import { registerLocaleData } from '@angular/common';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http';
import {
  MAT_PAGINATOR_DEFAULT_OPTIONS,
  MatPaginatorIntl
} from '@angular/material/paginator';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  popperVariation,
  provideTippyConfig,
  tooltipVariation
} from '@ngneat/helipopper';
import { provideHotToastConfig } from '@ngneat/hot-toast';
import { provideLoadingBarInterceptor } from '@ngx-loading-bar/http-client';
import { provideLoadingBarRouter } from '@ngx-loading-bar/router';
import { NgxsActionsExecutingModule } from '@ngxs-labs/actions-executing';
import { NgxsSelectSnapshotModule } from '@ngxs-labs/select-snapshot';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import {
  NgxsStoragePluginModule,
  SESSION_STORAGE_ENGINE
} from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { isEmpty } from 'lodash-es';
import localeFr from '@angular/common/locales/fr';
import { provideNativeDateAdapter } from '@angular/material/core';
import { BreadcrumbsModule } from '@exalif/ngx-breadcrumbs';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { provideNgxMask } from 'ngx-mask';
import { Observable } from 'rxjs';
import { NgxPermissionsModule } from 'ngx-permissions';

import { AuthFacade } from '~console/app/~store/facades/auth.facade';
import { GlobalFacade } from '~console/app/~store/facades/global.facade';
import {
  AuthState,
  DEFAULT_AUTH_STATE
} from '~console/app/~store/states/auth.state';
import { GlobalState } from '~console/app/~store/states/global.state';
import { environment } from '~environments/environment';
import { routes } from './app.routes';
import { CustomNgxsStorageEngine } from './~shared/utils/storage';
import { AccountService } from '~console/app/~shared/services/account.service';
import { CustomerSupplierService } from '~console/app/~shared/services/customer-supplier.service';
import { OrganizationService } from '~console/app/~shared/services/organization.service';
import { UserService } from '~console/app/~shared/services/user.service';
import { CoreModule } from '~core/modules/core.module';
import { StorageModule } from '~core/modules/storage.module';
import { AuditService } from '~core/services/audit.service';
import { BillingAddressService } from '~core/services/billing-address.service';
import { CompanyService } from '~core/services/company.service';
import { ConstantsService } from '~core/services/constants.service';
import { StatisticService } from '~core/services/statistic.service';
import { DateService } from '~core/services/date.service';
import { EstablishmentService } from '~core/services/establishment.service';
import { ImportTaskService } from '~core/services/import-task.service';
import { InvoiceService } from '~core/services/invoice.service';
import { ConversionRateService } from '~core/services/conversion-rate.service';
import { LoggerService } from '~core/services/logger.service';
import { PaymentService } from '~core/services/payment.service';
import { PaymentsService } from '~core/services/payments.service';
import { ReferenceDataService } from '~core/services/reference-data.service';
import { ReceiptService } from '~core/services/receipt.service';
import { RolesService } from '~core/services/roles.service';
import { SalesPointService } from '~core/services/sales-point.service';
import { StickerConsumptionService } from '~core/services/sticker-consumption.service';
import { StickerMovementService } from '~core/services/sticker-movement.service';
import { StickerService } from '~core/services/sticker.service';
import { ApiTokenService } from '~core/services/api-token.service';
import { TaxRulesService } from '~core/services/tax-rules.service';
import { ValidationTransactionService } from '~core/services/validation-transaction.service';
import {
  AUTH_STATE_TOKEN,
  GLOBAL_STATE_TOKEN
} from './~shared/constants/global.constants';
import { applicationIdInterceptor } from './~shared/interceptors/application-id.interceptor';
import { httpErrorsInterceptor } from './~shared/interceptors/http-errors.interceptors';
import { jwtInterceptor } from './~shared/interceptors/jwt.interceptor';
import { acceptLanguageInterceptor } from './~shared/interceptors/language.interceptor';
import { TaxMockService } from './~shared/mocks/tax-mock.service';
import { BreakpointsService } from './~shared/services/breakpoints.service';
import { IconService } from './~shared/services/icon.service';
import { MapService } from './~shared/services/map.service';
import { MatPaginatorI18nService } from './~shared/services/mat-paginator-i18n.service';
import { ProductService } from './~shared/services/product.service';
import { ProfileService } from './~shared/services/profile.service';
import { SsrRequestService } from './~shared/services/ssr-request.service';
import { StorageService } from './~shared/services/storage.service';
import { TaxService } from './~shared/services/tax.service';
import { ToastService } from './~shared/services/toast.service';
import { UnitMeasureService } from './~shared/services/unit-measure.service';
import { InvoiceFacade } from './~store/facades/invoice.facade';
import { ResourceFacade } from './~store/facades/resource.facade';
import { InvoiceState } from './~store/states/invoice.state';
import { ResourceState } from './~store/states/resource.state';
import { CurrenciesService } from '~core/services/currencies.service';
import { DashboardState } from './~store/states/dashboard.state';
import { DashboardFacade } from './~store/facades/dashboard.facade';
import { OrganizationState } from './~store/states/organization.state';
import { superAdminOrganizationIdInterceptor } from './~shared/interceptors/super-admin-organization-id.interceptor';
import { DashboardService } from '~core/services/dashboard.service';
import { BillingTerminalsService } from '~core/services/billing-terminals.service';
import { OrganizationFacade } from './~store/facades/organization.facade';
import { PhoneNumberModule } from '~ui-kit/inputs';
import { COUNTRIES_URL } from '~ui-kit/inputs/phone-number/constants';
import { CancellationRequestService } from '~core/services/cancellation-request.service';
import { CancellationAuditLogService } from '~core/services/cancellation-audit-log.service';
import { DeclaredTaxExemptionService } from '~core/services/declared-tax-exemption.service';
import { TaxExemptionTypeService } from '~core/services/tax-exemption-type.service';
import { DerogatoryRegimeService } from '~core/services/derogatory-regime.service';
import { JustificationDocTypeService } from '~core/services/justification-doc-type.service';
import { BeneficiaryCategoryService } from '~core/services/beneficiary-category.service';
registerLocaleData(localeFr, 'fr');

export const appConfig: ApplicationConfig = {
  providers: [
    provideExperimentalZonelessChangeDetection(),
    provideClientHydration(),
    provideRouter(routes),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        jwtInterceptor,
        superAdminOrganizationIdInterceptor,
        httpErrorsInterceptor,
        applicationIdInterceptor,
        acceptLanguageInterceptor
      ]),
      withInterceptorsFromDi()
    ),
    provideLoadingBarInterceptor(),
    provideLoadingBarRouter(),
    provideAnimations(),
    provideNgxMask(),
    { provide: LOCALE_ID, useValue: 'fr' },
    provideHotToastConfig({
      visibleToasts: 1
    }),
    provideAngularSvgIcon(),
    provideTippyConfig({
      defaultVariation: 'tooltip',
      variations: {
        tooltip: tooltipVariation,
        popper: popperVariation
      }
    }),
    provideNativeDateAdapter(),
    CustomNgxsStorageEngine,
    importProvidersFrom([
      NgxsModule.forRoot(
        [
          GlobalState,
          AuthState,
          ResourceState,
          InvoiceState,
          DashboardState,
          OrganizationState
        ],
        {
          developmentMode: !environment.production
        }
      ),
      NgxsActionsExecutingModule.forRoot(),
      NgxsSelectSnapshotModule.forRoot(),
      NgxsFormPluginModule.forRoot(),
      NgxsStoragePluginModule.forRoot({
        keys: [
          GlobalState,
          {
            key: AuthState,
            engine: CustomNgxsStorageEngine
          },
          {
            key: DashboardState,
            engine: SESSION_STORAGE_ENGINE
          }
        ],
        beforeSerialize: (obj, key) => {
          if (key === GLOBAL_STATE_TOKEN.getName()) {
            // Save only provided properties on storage
            return {
              routeData: { currentUrl: obj?.routeData?.currentUrl },
              currentResource: obj?.currentResource,
              currentOrganization: obj?.currentOrganization
            };
          }
          if (key === AUTH_STATE_TOKEN.getName()) {
            return isEmpty(obj)
              ? DEFAULT_AUTH_STATE
              : {
                  currentUser: obj?.currentUser,
                  tokens: obj?.tokens,
                  login: obj?.login
                    ? {
                        ...obj?.login,
                        refreshTokenId: undefined,
                        organizationIdToConnectTo: undefined
                      }
                    : undefined,
                  registration: DEFAULT_AUTH_STATE.registration,
                  onboarding: obj?.onboarding,
                  reset: DEFAULT_AUTH_STATE.reset
                };
          }
          return obj;
        }
      }),
      NgxsReduxDevtoolsPluginModule.forRoot(),
      LayoutModule,
      CoreModule,
      StorageModule,
      BreadcrumbsModule.forRoot(),
      NgxPermissionsModule.forRoot(),
      PhoneNumberModule
    ]),
    provideAppInitializer((): Observable<unknown> | void => {
      const constantsService = inject(ConstantsService);

      if (!environment.local) {
        return constantsService.getRemoteEnvironmentVariables();
      }
    }),
    {
      provide: COUNTRIES_URL,
      useValue: 'assets/constants/country-list.json'
    },
    BreakpointsService,
    StorageService,
    SsrRequestService,
    GlobalFacade,
    AuthFacade,
    ResourceFacade,
    DashboardFacade,
    InvoiceFacade,
    ProfileService,
    ToastService,
    IconService,
    DateService,
    CompanyService,
    AccountService,
    UnitMeasureService,
    ProductService,
    { provide: TaxService, useClass: TaxMockService },
    { provide: MatPaginatorIntl, useClass: MatPaginatorI18nService },
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: {
        pageSize: 10,
        pageSizeOptions: [10, 25, 50, 100],
        showFirstLastButtons: true
      }
    },
    OrganizationService,
    MapService,
    LoggerService,
    ReferenceDataService,
    RolesService,
    UserService,
    AuditService,
    ImportTaskService,
    EstablishmentService,
    SalesPointService,
    StickerService,
    StickerConsumptionService,
    PaymentsService,
    StickerMovementService,
    ApiTokenService,
    InvoiceService,
    TaxRulesService,
    ValidationTransactionService,
    CurrenciesService,
    ConversionRateService,
    CustomerSupplierService,
    BillingAddressService,
    DashboardService,
    PaymentService,
    ReceiptService,
    StatisticService,
    PaymentService,
    BillingTerminalsService,
    OrganizationFacade,
    CancellationRequestService,
    CancellationAuditLogService,
    DeclaredTaxExemptionService,
    TaxExemptionTypeService,
    DerogatoryRegimeService,
    BeneficiaryCategoryService,
    JustificationDocTypeService,
    OrganizationFacade
  ]
};
